import {
  CompanyCompetitorsWithThemesResponse,
  CompanyCountryExposureResponse,
  CompanyRegionExposureResponse,
  ThemeChildrenResponse,
  ThemeCompaniesResponse,
  ThemeCompetitorResponse,
  ThemeExposureResponse,
} from '@api';
import { getTimeTillEndOfDayMs } from '@helpers';
import { useQuery } from '@tanstack/react-query';
import {
  fetchCompanyCompetitorsWithThemes,
  fetchCompanyCountryExposure,
  fetchCompanyRegionExposure,
  fetchCompanyTheme,
  fetchCompanyThemeCompetitors,
  fetchThemeChildren,
  fetchThemeCompanies,
} from '@terminal/services/overview-widget/overview-widget-service';
import { WretchError } from 'wretch/types';

import { useEntities } from '../entities/useEntities';

export const QUERY_KEY_COMPANY_THEME = 'QUERY_KEY_COMPANY_THEME';
export const useCompanyTheme = ({
  entityTag,
  limit,
  enabled = true,
}: {
  entityTag: string;
  limit?: number;
  enabled?: boolean;
}) =>
  useQuery<ThemeExposureResponse, WretchError>({
    queryKey: [QUERY_KEY_COMPANY_THEME, entityTag, limit],
    queryFn: ({ signal }) => fetchCompanyTheme({ entityTag, limit, signal }),
    staleTime: getTimeTillEndOfDayMs(),
    enabled: enabled && !!entityTag,
  });

export const QUERY_KEY_COMPANY_THEME_COMPETITORS =
  'QUERY_KEY_COMPANY_THEME_COMPETITORS';
export const useCompanyThemeCompetitors = ({
  entityTag,
  themeId,
  limit,
}: {
  entityTag?: string;
  themeId?: string;
  limit?: number;
  enabled?: boolean;
}) =>
  useQuery<ThemeCompetitorResponse, WretchError>({
    queryKey: [QUERY_KEY_COMPANY_THEME_COMPETITORS, entityTag, themeId, limit],
    queryFn: ({ signal }) =>
      fetchCompanyThemeCompetitors({
        entityTag: entityTag!,
        themeId: themeId!,
        limit,
        signal,
      }),
    staleTime: getTimeTillEndOfDayMs(),
    enabled: !!entityTag && !!themeId,
  });

export const QUERY_KEY_THEME_COMPANIES = 'QUERY_KEY_THEME_COMPANIES';
export const useThemeCompanies = ({
  themeId,
  limit,
  enabled = true,
}: {
  themeId: string;
  limit?: number;
  enabled?: boolean;
}) =>
  useQuery<ThemeCompaniesResponse, WretchError>({
    queryKey: [QUERY_KEY_THEME_COMPANIES, themeId, ...(!!limit ? [limit] : [])],
    queryFn: ({ signal }) => fetchThemeCompanies({ themeId, limit, signal }),
    staleTime: getTimeTillEndOfDayMs(),
    enabled: enabled,
  });

export const QUERY_KEY_THEME_SIBLINGS = 'QUERY_KEY_THEME_SIBLINGS';
export const useThemeChildren = (themeId: string, entityTag?: string) =>
  useQuery<ThemeChildrenResponse, WretchError>({
    queryKey: [QUERY_KEY_THEME_SIBLINGS, themeId, entityTag],
    queryFn: ({ signal }) => fetchThemeChildren(themeId, entityTag, signal),
    staleTime: getTimeTillEndOfDayMs(),
    enabled: !!themeId,
  });

export const QUERY_KEY_COMPANY_COMPETITOR_WITH_THEMES =
  'QUERY_KEY_COMPETITOR_WITH_THEMES';
export const useCompanyCompetitorWithThemes = ({
  entityTag,
  limit,
  enabled = true,
}: {
  entityTag: string;
  limit?: number;
  enabled?: boolean;
}) =>
  useQuery<CompanyCompetitorsWithThemesResponse, WretchError>({
    queryKey: [
      QUERY_KEY_COMPANY_COMPETITOR_WITH_THEMES,
      entityTag,
      ...(limit ? [limit] : []),
    ],
    queryFn: ({ signal }) =>
      fetchCompanyCompetitorsWithThemes({ entityTag, limit, signal }),
    staleTime: getTimeTillEndOfDayMs(),
    enabled: enabled && !!entityTag,
  });

export const QUERY_KEY_COMPANY_COUNTRY_EXPOSURE =
  'QUERY_KEY_COMPANY_COUNTRY_EXPOSURE';
export const useCompanyCountryExposure = ({
  entityTag,
  enabled = true,
}: {
  entityTag: string;
  enabled?: boolean;
}) =>
  useQuery<CompanyCountryExposureResponse, WretchError>({
    queryKey: [QUERY_KEY_COMPANY_COUNTRY_EXPOSURE, entityTag],
    queryFn: ({ signal }) => fetchCompanyCountryExposure({ entityTag, signal }),
    staleTime: getTimeTillEndOfDayMs(),
    enabled: enabled && !!entityTag,
  });

export const QUERY_KEY_COMPANY_REGION_EXPOSURE =
  'QUERY_KEY_COMPANY_REGION_EXPOSURE';
export const useCompanyRegionExposure = ({
  entityTag,
  enabled = true,
}: {
  entityTag: string;
  enabled?: boolean;
}) =>
  useQuery<CompanyRegionExposureResponse, WretchError>({
    queryKey: [QUERY_KEY_COMPANY_REGION_EXPOSURE, entityTag],
    queryFn: ({ signal }) => fetchCompanyRegionExposure({ entityTag, signal }),
    staleTime: getTimeTillEndOfDayMs(),
    enabled: enabled && !!entityTag,
  });

export const useCompanyCompetitors = ({
  entityTag,
  limit,
  themeFilterId,
  enabled = true,
}: {
  entityTag: string;
  limit?: number;
  /** When set, loads competitors scoped to this theme (ontology theme filter). */
  themeFilterId?: string;
  /** When false, skips competitor and related entity fetches (e.g. accordion collapsed). */
  enabled?: boolean;
}) => {
  const themeScoped = !!themeFilterId?.trim();
  const queriesEnabled = enabled && !!entityTag.trim();

  const {
    data: themeCompetitorResponse,
    isFetching: isFetchingThemeCompetitors,
    isError: isThemeCompetitorsError,
    error: themeCompetitorsError,
  } = useCompanyThemeCompetitors({
    entityTag,
    themeId: themeFilterId ?? '',
    limit,
    enabled: themeScoped && queriesEnabled,
  });

  const competitorTagsFromTheme = themeScoped
    ? (themeCompetitorResponse?.competitors ?? [])
    : [];

  const {
    data: competitorsWithThemesData = [],
    isFetching: isFetchingCompetitorsWithThemes,
    isError: isCompetitorsError,
    error: competitorsError,
  } = useCompanyCompetitorWithThemes({
    entityTag,
    limit,
    enabled: !themeScoped && queriesEnabled,
  });

  const entityTagsForFetch = themeScoped
    ? competitorTagsFromTheme
    : competitorsWithThemesData.map(s => s.company);

  const {
    data: competitorEntitiesData = [],
    isLoading: isLoadingCompetitorEntitiesData,
    isError: isEntitiesError,
  } = useEntities({
    entityTags: entityTagsForFetch,
    enabled: entityTagsForFetch.length > 0 && queriesEnabled,
  });

  const competitorsData = competitorEntitiesData.map(e => ({ entity: e }));

  const isFetching = themeScoped
    ? isFetchingThemeCompetitors || isLoadingCompetitorEntitiesData
    : isFetchingCompetitorsWithThemes || isLoadingCompetitorEntitiesData;

  const isError = themeScoped
    ? isThemeCompetitorsError || isEntitiesError
    : isCompetitorsError || isEntitiesError;

  const error = themeScoped ? themeCompetitorsError : competitorsError;

  return {
    competitorsData,
    isFetching,
    isError,
    error,
  };
};
